import type {
  AssignedInternalProject,
  AssignedSchool,
  ProjectResourceResponse,
  ProjectResponse,
  ProjectResponseDetailedApplied,
  ProjectApplicationDetailedApplied,
  TaskRaw,
} from '@/types';

export const getMentorName = (p: ProjectResponse | ProjectApplicationDetailedApplied | null | undefined) => {
  if (!p) return '';

  if ('project_mentor' in p && typeof p.project_mentor === 'string') {
    return p.project_mentor;
  }

  // If it's ProjectApplicationDetailedApplied
  const lead = (p as unknown as Record<string, unknown>).project_lead;
  if (lead && typeof lead === 'object' && 'email' in lead) {
    return (lead as Record<string, unknown>).email as string || '';
  }

  return '';
};

export const statusTone = (status?: string | number) => {
  if (typeof status === 'number')
    return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  const s = (status ?? '').toString().toLowerCase();

  if (s.includes('active'))
    return 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300';
  if (s.includes('completed') || s.includes('done'))
    return 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-300';
  if (s.includes('pending') || s.includes('not'))
    return 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300';

  return 'border-border bg-muted text-foreground/80';
};

export const mapExternalProject = (
  external: any
): ProjectResponse => {
  if (!external) return {} as ProjectResponse;


  const details = external.project_details || external || {};

  return {
    id: external.id,
    project_id: external.project_ref?.id || external.project_id || undefined,
    project_resorces_id: external.project_resorces_id || external.project_resources_id || undefined,

    // external doesn't send this, so we derive or default
    school_id: external.assigned_schools?.map((s: any) => s.tenant_id ?? s) ?? [],
    assigned_projects: null,
    project_mentor:
      external.public_project_candidates?.find((c: any) => c.role === 1)?.candidate?.email ??
      external.assigned_to?.email ??
      external.assigned_to?.candidate_email ??
      null,
    project_status: external.project_status ?? 0,
    project_skill_level: [],
    number_of_docs: (details.resources ?? external.resources)?.length ?? 0,
    number_of_enrollments: 0,
    number_of_forums: 0,

    name: details.name || external.name || '',

    project_type: 2,
    project_structure: (external.project_structure ?? details.project_structure ?? 2) as 1 | 2,

    methodology: details.methodology ?? external.methodology ?? null,
    scope_of_work: details.scope ?? details.scope_of_work ?? external.scope_of_work ?? null,
    expected_outcomes: details.expected_outcomes ?? external.expected_outcomes ?? null,
    objectives: details.objectives ?? external.objectives ?? null,
    expected_deliverables: details.expected_deliverables ?? external.expected_deliverables ?? null,

    start_date: details.start_date ?? external.start_date ?? external.started_at ?? null,
    end_date: details.end_date ?? external.end_date ?? external.ends_at ?? null,
    description: details.description ?? external.description ?? null,

    required_team_members: details.required_team_members ?? external.required_team_members ?? null,
    terms_and_conditions: details.terms_and_conditions ?? external.terms_and_conditions ?? null,

    created_on: details.created_on ?? external.created_on ?? external.created_date ?? new Date().toISOString(),
    updated_on: details.updated_on ?? external.updated_on ?? new Date().toISOString(),

    assigned_schools: external.assigned_schools ?? [],

    assign_to_multiple_schools: details.multiple_schools ?? details.assign_to_multiple_schools ?? external.assign_to_multiple_schools ?? false,

    sdgs: details.sdgs ?? external.sdgs ?? null,
    status: external.status ?? details.status ?? 1,

    project_duration: details.project_duration ?? external.project_duration ?? 0,
    project_duration_type: details.project_duration_type ?? external.project_duration_type ?? 1,

    project_banner: details.project_banner ?? external.project_banner ?? null,
    published: details.published ?? external.published ?? false,

    created_by: details.created_by ?? external.created_by ?? '',
    board: null,
    tasks_completed_percentage: external.tasks_completed_percentage ?? 0,
    members: external.assigned_to ? [external.assigned_to] : [],

    expected_skills: (details.expected_skills || external.expected_skills || []).map((s: any) =>
      typeof s === 'string' ? { id: 0, skill: 0, skill_name: s, required_level: 1 } : s
    ),
    skill_framework: (details.skill_framework ?? external.skill_framework ?? []).map((s: any, i: number) => ({
      id: s?.id ?? i,
      name: s?.name ?? s ?? '',
    })),
    global_skill_framework: (details.global_skill_framework ?? external.global_skill_framework ?? []).map(
      (g: any) => ({
        id: g?.id ?? g,
        name: g?.name ?? `Skill ${g?.id ?? g}`,
      })
    ),

    resources:
      (details.resources ?? external.resources ?? details.project_resources ?? external.project_resources)?.map((r: any) => ({
        id: r.id ?? 0,
        description: r.description ?? null,
        created_on: r.created_on ?? new Date().toISOString(),
        updated_on: r.updated_on ?? new Date().toISOString(),
        path: r.path ?? r.url ?? '',
        size: r.size ?? '',
        name: r.name ?? '',
        type: r.type ?? '',
        project: r.project ?? external.id ?? 0,
      })) ?? [],
    public_project_candidates: external.public_project_candidates ?? [],
    project_ref: external.project_ref,
  };
};

export const getStatusLabel = (status: string | number) => {
  const s = Number(status);
  if (s === 1) return 'Pending';
  if (s === 2) return 'Assigned';
  if (s === 3) return 'Ready to Start';
  if (s === 4) return 'In Progress';
  if (s === 5) return 'Completed';
  return status.toString();
};

export const getStatusColor = (status: string | number) => {
  const statusLower = getStatusLabel(status).toLowerCase();
  if (statusLower === 'assigned')
    return 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20';
  if (statusLower === 'in progress')
    return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20';
  if (statusLower === 'completed')
    return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20';
  if (statusLower === 'pending' || statusLower === 'ready to start')
    return 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20';
  return 'bg-muted text-muted-foreground border-border';
};

export const getDurationLabel = (duration: number, type: number) => {
  const unit = type === 1 ? 'days' : type === 2 ? 'weeks' : 'months';
  return `${duration} ${unit}`;
};

// adapter: DetailedApplied[]  ->  ProjectResponse[]
// (keeps your existing ProjectResponse shape)

export function toProjectResponseArray(
  detailed: AssignedInternalProject[] | ProjectResponseDetailedApplied | undefined | null
): ProjectResponse[] {
  if (!detailed?.length) return [];

  const items = detailed as Array<ProjectApplicationDetailedApplied>;

  return items.map<ProjectResponse>((item) => {
    const top = item.project_details;
    const p = item.project;
    const inner = p?.project_details;

    return {
      project_resorces_id: item.id,
      id: p?.company_project_id ?? item.id,
      project_id: p?.id,
      school_id: top?.assigned_to ?? [],
      assigned_projects: null,
      project_mentor: p?.project_lead_email ?? item.candidate_email ?? null,
      project_status: item.project_status ?? item.status ?? 1,
      project_skill_level: [],

      number_of_docs: item.number_of_docs ?? 0,
      number_of_enrollments: item.number_of_enrollments ?? 0,
      number_of_forums: item.number_of_forums ?? 0,

      name: top?.name ?? inner?.name ?? '',

      project_type: (p?.project_type ?? 1) as 1 | 2,

      project_structure: 1,

      methodology: inner?.methodology ?? null,

      scope_of_work: inner?.scope ?? top?.scope ?? null,

      expected_outcomes: inner?.expected_outcomes ?? null,
      objectives: inner?.objectives ?? top?.objectives ?? null,
      expected_deliverables: inner?.expected_deliverables ?? null,

      start_date: p?.started_at ?? null,
      end_date: inner?.end_date ?? null,
      description: inner?.description ?? null,

      required_team_members: inner?.required_team_members ?? null,
      terms_and_conditions: inner?.terms_and_conditions ?? null,

      created_on: inner?.created_on ?? p?.createdAt ?? '',
      updated_on: inner?.updated_on ?? p?.updatedAt ?? '',

      assigned_schools: [],
      assign_to_multiple_schools: top?.multiple_schools ?? false,

      sdgs: top?.sdgs || null,

      status: top?.status ?? p?.project_status ?? item.project_status ?? item.status ?? 1,

      project_duration: p?.project_duration ?? 0,
      project_duration_type: p?.project_duration_type ?? 1,

      project_banner: top?.banner_url ?? null,

      published: false,

      created_by: p?.project_lead_email ?? item.candidate_email ?? '',
      board: null,

      expected_skills: [],

      skill_framework: (top?.skill_framework ?? []).map((s) => ({
        id: s.id ?? 0,
        name: s.name ?? "",
      })),

      global_skill_framework: (top?.global_skill_framework ?? []).map((g) => ({
        id: g.id ?? 0,
        name: g.name ?? "",
      })),

      resources: (p?.project_resources ?? []).map((r: any) => ({
        id: r.id ?? 0,
        description: r.description ?? null,
        created_on: r.created_on ?? new Date().toISOString(),
        updated_on: r.updated_on ?? new Date().toISOString(),
        path: r.path ?? '',
        size: r.size ?? '',
        name: r.name ?? '',
        type: r.type ?? '',
        project: r.project ?? p?.id ?? 0,
      })),
    };
  });
}


// ============================================================================
// UTILITIES
// ============================================================================

export function normalizeTaskStatus(t: TaskRaw): 'todo' | 'doing' | 'done' {
  const s = t.status;

  if (typeof s === 'string') {
    const v = s.toLowerCase();
    if (['todo', 'to_do', 'backlog', 'open', 'new'].includes(v)) return 'todo';
    if (['in_progress', 'doing', 'wip', 'started'].includes(v)) return 'doing';
    if (['done', 'completed', 'closed', 'resolved'].includes(v)) return 'done';
  }

  if (typeof s === 'number') {
    if ([0, 1].includes(s)) return 'todo';
    if (s === 2) return 'doing';
    if (s === 3 || s === 4 || s === 5) return 'done';
  }

  return 'todo';
}

export function getTaskTitle(t: TaskRaw) {
  return t.tasks || t.title || t.name || `Task #${t.id}`;
}

export function countsFromTasks(tasks: TaskRaw[]) {
  const counts = { todo: 0, doing: 0, done: 0 };
  for (const t of tasks) counts[normalizeTaskStatus(t)]++;
  return counts;
}

export function progressFromCounts(counts: {
  todo: number;
  doing: number;
  done: number;
}) {
  const total = counts.todo + counts.doing + counts.done;
  if (!total) return 0;
  return Math.round((counts.done / total) * 100);
}

export function headerMetricLabel(status: 'todo' | 'doing' | 'done') {
  if (status === 'todo') return 'To do';
  if (status === 'doing') return 'In progress';
  return 'Done';
}

export function normalizeProjectDocs(docs?: ProjectResourceResponse) {
  if (!docs) return [];
  return docs.map((doc) => ({
    id: doc.id,
    name: doc.name || 'Untitled Document',
    document: doc.path || '', // URL or empty string
    description: doc.description || '',
    type: doc.type || 'unknown',
    size: doc.size || '',
    created_on: doc.created_on,
    updated_on: doc.updated_on,
  }));
}


/**
 * Normalizes the new project response format to match ProjectResponse interface
 */
/**
 * Normalizes the new project response format to match ProjectResponse interface
 */
export function normalizeProjectDetailsResponse(data: unknown): ProjectResponse {
  if (!data) return {} as ProjectResponse;

  const item = data as ProjectApplicationDetailedApplied;
  const project = item.project || {};
  const details = item.project_details || {};
  const innerDetails = project.project_details || {};
  const company = item.company || project.company || {};

  // Safely access properties that might be missing from the official types but present in the API
  const rawProject = project as unknown as Record<string, unknown>;

  return {
    id: project.company_project_id || item.id || 0,
    project_id: project.id,
    name: details.name || innerDetails.name || "",
    project_status: item.project_status || "PENDING",
    status: item.status || "pending",
    description: innerDetails.description || null,
    scope_of_work: innerDetails.scope || details.scope || null,
    objectives: innerDetails.objectives || details.objectives || null,
    methodology: innerDetails.methodology || null,
    expected_outcomes: innerDetails.expected_outcomes || null,
    expected_deliverables: innerDetails.expected_deliverables || null,
    project_duration: project.project_duration || 30, // Default to 30 based on JSON
    project_duration_type: project.project_duration_type || 1,
    project_banner: details.banner_url || (rawProject.project_banner as string | null) || null,
    published: details.status === 2 || item.status_display === "Published",
    evaluation_required:
      (item as unknown as Record<string, unknown>).evaluation_required ??
      (project as unknown as Record<string, unknown>).evaluation_required ??
      false,

    assigned_schools: (rawProject.assigned_schools as AssignedSchool[] | AssignedSchool) || {
      id: project.company_id || 0,
      name: company.name || "School",
      logo_url: company.logo_url || "",
      website: company.website || "",
      address: "",
      about_us: (company as unknown as Record<string, unknown>).about_us as string | null || null,
      tenant_type: 'school',
      business_area: [],
      profile_setup: true,
      created_on: project.createdAt || "",
      slogan: (company as unknown as Record<string, unknown>).slogan as string | null || null,
      email: project.project_lead?.email || null,
      primary_color: (company as unknown as Record<string, unknown>).primary_color as string | null || null,
      tenant_id: project.company_id || 0,
      country: (company as unknown as Record<string, unknown>).country as string | null || null,
      telephone: ""
    } as AssignedSchool,

    sdgs: (details.sdgs as (string | number)[] | null) || [],
    expected_skills: (innerDetails.skills || []).map((s: any) => {
      const skillId = s.skill_id || 0;
      return {
        ...s,
        id: s.id || skillId, // Preserve mapping id for deletion, fallback to skill_id for keys
        skill_id: skillId,
        skill_name: s.skill_name || "Unknown Skill",
        required_level: s.required_level || 1
      };
    }),
    skill_framework: (details.skill_framework || []).map((s) => ({
      id: s.id ?? 0,
      name: s.name || "Unknown Skill",
    })),
    global_skill_framework: (details.global_skill_framework || []).map(
      (g) => ({
        id: g.id ?? 0,
        name: g.name || `Skill ${g.id || 0}`,
      })
    ),

    number_of_enrollments: item.number_of_enrollments || 0,
    number_of_docs: item.number_of_docs || 0,
    number_of_forums: item.number_of_forums || 0,

    project_mentor: item.role_display === "MENTOR" ? item.candidate_email : (project.project_lead?.email || null),

    school_id: project.company_id ? [project.company_id] : [],
    assigned_projects: null,
    project_skill_level: [],
    project_type: (project.project_type || 1) as 1 | 2,
    project_structure: (rawProject.project_structure as 1 | 2) || 1,
    start_date: project.started_at || null,
    end_date: innerDetails.end_date || null,
    required_team_members: innerDetails.required_team_members || null,
    terms_and_conditions: innerDetails.terms_and_conditions || null,
    created_on: innerDetails.created_on || project.createdAt || "",
    updated_on: innerDetails.updated_on || project.updatedAt || "",
    assign_to_multiple_schools: details.multiple_schools || false,
    created_by: (project.project_lead?.id || "").toString(),
    board: null,
    project_management_project: project.project_management_project || [],
    members: project.project || [],
    tasks_completed_percentage: item.tasks_completed_percentage ?? 0,
    docs_project: project.docs_project || [],
    resources: (project.project_resources || []).map((r: any) => ({
      id: r.id ?? 0,
      description: r.description ?? null,
      created_on: r.created_on ?? new Date().toISOString(),
      updated_on: r.updated_on ?? new Date().toISOString(),
      path: r.path ?? r.url ?? '',
      size: r.size ?? '',
      name: r.name ?? '',
      type: r.type ?? '',
      project: r.project ?? project.id ?? 0,
    })),
    public_project_candidates: (data as any).public_project_candidates || [],
  };
}
